import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextField,
  InputLabel,
} from "@mui/material";
import "../../styles/inputBox.css";
import { putRequest } from "../../api/apiServices";
import { updateJob } from "../../api/apiList";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { roleBasedRedirection } from "../../utils/helper";
import Loader from "../Loader";
import { VonqMapping } from "../../types/vonqMapping";
import {
  finalizeJobSpecVonqMapping,
  mapSeniorityToExperienceLevel,
} from "../../utils/contractTypeMapping";
import {
  buildJobProfileSecondSnapshot,
  isSelfEmittedFormUpdate,
  vonqMappingsEqual,
} from "../../utils/jobSpecFormSync";
import { VonqTaxonomySelect } from "./vonq";

interface JobProfileSecondProps {
  onBack: () => void;
  onNext: () => void; // Function to navigate to the next component
  setFormData: (data: any) => void;
  initialData: any;
  mode: any; //
}

const JobProfileSecond: React.FC<JobProfileSecondProps> = ({
  onBack,
  onNext,
  setFormData,
  initialData,
  mode,
}: JobProfileSecondProps) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const isTabs = useMediaQuery("(max-width:1200px)");

  const [taskOfThisPosition, setTaskOfThisPosition] = useState("");
  const [anyProjectsOfCandidates, setAnyProjectsOfCandidates] = useState("");
  const [educationAndExperience, setEducationAndExperience] = useState("");
  const [technicalSkillsRequired, setTechnicalSkillsRequired] = useState("");
  const [softSkillsRequired, setSoftSkillsRequired] = useState("");
  const [preferredCertificationsLicenses, setPreferredCertificationsLicenses] =
    useState("");
  const [idealExperienceLevel, setIdealExperienceLevel] = useState("");
  const [
    industrySpecificExperienceRequired,
    setIndustrySpecificExperienceRequired,
  ] = useState("");
  const [loading, setLoading] = useState(false);
  const [vonqMapping, setVonqMapping] = useState<VonqMapping>(
    initialData?.vonqMapping || {}
  );

  const lastEmittedSnapshotRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const loadedJobIdRef = useRef<string | number | null>(null);

  const updateVonqMapping = (patch: Partial<VonqMapping>) => {
    setVonqMapping((prev) => ({
      ...prev,
      ...patch,
      syncSource: "job_spec_form",
      syncedAt: new Date().toISOString(),
    }));
  };

  const mergeVonqMappingForSave = () =>
    finalizeJobSpecVonqMapping({
      ...initialData,
      vonqMapping,
    });

  //params to get id
  const params = useParams();
  const navigate = useNavigate();

  const goBack = () => {
    // navigate("/jobs");
    navigate(roleBasedRedirection("/jobs"));
  };

  const [errors, setErrors] = useState({
    taskOfThisPosition: "",
    anyProjectsOfCandidates: "",
    educationAndExperience: "",
    vonqEducationLevel: "",
    technicalSkillsRequired: "",
    softSkillsRequired: "",
    preferredCertificationsLicenses: "",
    idealExperienceLevel: "",
    vonqIndustry: "",
  });

  // Sync local state with initialData when loading a job or external updates only
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) {
      return;
    }

    const incomingSnapshot = buildJobProfileSecondSnapshot(initialData);
    if (isSelfEmittedFormUpdate(incomingSnapshot, lastEmittedSnapshotRef)) {
      hasHydratedRef.current = true;
      return;
    }

    const jobId = initialData.id as string | number | undefined;
    const isNewExternalJobLoad =
      jobId != null && loadedJobIdRef.current !== jobId;
    const shouldHydrate = isNewExternalJobLoad || !hasHydratedRef.current;

    if (!shouldHydrate) {
      return;
    }

    if (jobId != null) {
      loadedJobIdRef.current = jobId;
    }

    if (initialData?.taskOfThisPosition !== undefined) {
      setTaskOfThisPosition(initialData.taskOfThisPosition || "");
    }
    if (initialData?.anyProjectsOfCandidates !== undefined) {
      setAnyProjectsOfCandidates(initialData.anyProjectsOfCandidates || "");
    }
    if (initialData?.educationAndExperience !== undefined) {
      setEducationAndExperience(initialData.educationAndExperience || "");
    }
    if (initialData?.technicalSkillsRequired !== undefined) {
      setTechnicalSkillsRequired(initialData.technicalSkillsRequired || "");
    }
    if (initialData?.softSkillsRequired !== undefined) {
      setSoftSkillsRequired(initialData.softSkillsRequired || "");
    }
    if (initialData?.preferredCertificationsLicenses !== undefined) {
      setPreferredCertificationsLicenses(initialData.preferredCertificationsLicenses || "");
    }
    if (initialData?.idealExperienceLevel !== undefined) {
      setIdealExperienceLevel(initialData.idealExperienceLevel || "");
    }
    if (initialData?.industrySpecificExperienceRequired !== undefined) {
      setIndustrySpecificExperienceRequired(initialData.industrySpecificExperienceRequired || "");
    }
    if (initialData?.vonqMapping) {
      setVonqMapping(initialData.vonqMapping);
      if (initialData.vonqMapping.seniority?.description) {
        setIdealExperienceLevel(
          mapSeniorityToExperienceLevel(
            initialData.vonqMapping.seniority.description
          )
        );
      }
      if (
        initialData.vonqMapping.industry?.description &&
        !initialData.industrySpecificExperienceRequired
      ) {
        setIndustrySpecificExperienceRequired(
          initialData.vonqMapping.industry.description
        );
      }
    }

    hasHydratedRef.current = true;
  }, [initialData]);

  // Update formData whenever any field changes to ensure all data is available when navigating to other steps
  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    const hasChanges = 
      taskOfThisPosition !== (initialData?.taskOfThisPosition || "") ||
      anyProjectsOfCandidates !== (initialData?.anyProjectsOfCandidates || "") ||
      educationAndExperience !== (initialData?.educationAndExperience || "") ||
      technicalSkillsRequired !== (initialData?.technicalSkillsRequired || "") ||
      softSkillsRequired !== (initialData?.softSkillsRequired || "") ||
      preferredCertificationsLicenses !== (initialData?.preferredCertificationsLicenses || "") ||
      idealExperienceLevel !== (initialData?.idealExperienceLevel || "") ||
      industrySpecificExperienceRequired !== (initialData?.industrySpecificExperienceRequired || "") ||
      !vonqMappingsEqual(vonqMapping, initialData?.vonqMapping);

    if (hasChanges) {
      const currentData = {
        ...initialData,
        taskOfThisPosition,
        anyProjectsOfCandidates,
        educationAndExperience,
        technicalSkillsRequired,
        softSkillsRequired,
        preferredCertificationsLicenses,
        idealExperienceLevel,
        industrySpecificExperienceRequired,
        vonqMapping,
      };
      lastEmittedSnapshotRef.current = buildJobProfileSecondSnapshot(currentData);
      setFormData(currentData);
    }
  }, [taskOfThisPosition, anyProjectsOfCandidates, educationAndExperience, technicalSkillsRequired, softSkillsRequired, preferredCertificationsLicenses, idealExperienceLevel, industrySpecificExperienceRequired, vonqMapping, initialData, setFormData]);

  const handleBack = (event: any) => {
    event.preventDefault();
    // Handle form submission logic here
    onBack(); // Call the onNext function to navigate to the next component
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const data = {
      taskOfThisPosition,
      anyProjectsOfCandidates,
      educationAndExperience,
      technicalSkillsRequired,
      softSkillsRequired,
      preferredCertificationsLicenses,
      idealCandidateProfile: initialData?.idealCandidateProfile || "",
      idealExperienceLevel,
      industrySpecificExperienceRequired,
      vonqMapping: mergeVonqMappingForSave(),
    };
    if (formIsValid()) {
      if (mode) {
        setLoading(true);
        const response = await putRequest(updateJob(params?.id), data);
        setFormData(response?.data?.data);
        setLoading(false);
        onNext();
      }
      const allData = {
        ...initialData,
        ...data,
      };
      setFormData(allData);
      onNext(); // Call the onNext function to navigate to the next component
    }
  };

  const formIsValid = () => {
    const newErrors = {
      taskOfThisPosition: !taskOfThisPosition?.trim()
        ? "Primary Responsibilities is required"
        : "",
      anyProjectsOfCandidates: !anyProjectsOfCandidates?.trim()
        ? "Specific Projects or Initiativesr is required"
        : "",
      educationAndExperience: !educationAndExperience?.trim()
        ? "Qualifications is required"
        : "",
      vonqEducationLevel: !vonqMapping?.educationLevel?.vonqId
        ? "Please select an education level"
        : "",
      technicalSkillsRequired: !technicalSkillsRequired?.trim()
        ? "Technical Skills is required"
        : "",
      softSkillsRequired: !softSkillsRequired?.trim()
        ? "Soft Skills is required"
        : "",
      preferredCertificationsLicenses: !preferredCertificationsLicenses?.trim()
        ? "Certifications is required"
        : "",
      idealExperienceLevel: !vonqMapping?.seniority?.vonqId
        ? "Please select an experience level"
        : "",
      vonqIndustry: !vonqMapping?.industry?.vonqId
        ? "Please select an industry"
        : "",
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === "");
  };

  return (
    <div>
      <Container
        style={{
          display: "flex",
          padding: "25px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="884px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <div className="col-12 pb-1"></div>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="10px"
            >
              <Grid
                item
                width="fit-content"
                display="flex"
                alignItems="center"
                gap="10px"
              >
                <ArrowBackIcon onClick={goBack} style={{ cursor: "pointer" }} />
                <Typography color="#273848" fontWeight={700} fontSize="20.94px">
                  Responsibilities & Qualifications
                </Typography>
              </Grid>
              <Grid item gap={2} display="flex">
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#84E1CC",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#DFF4EF",
                  }}
                />
              </Grid>
            </Grid>
            <Grid my={2}></Grid>
            <Grid mt="22px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
            >
              Key Responsibilities
            </Typography>
            <Grid mt="19px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What are the primary responsibilities and tasks associated with
                this position? <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                    padding: "12px 11px", 
                  },
                }}
                placeholder="What are the primary responsibilities and tasks associated with this position?"
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.taskOfThisPosition}
                helperText={errors.taskOfThisPosition}
                value={taskOfThisPosition}
                onChange={(e) => {
                  const value = e.target.value;
                  setTaskOfThisPosition(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      taskOfThisPosition: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Are there any specific projects or initiatives the candidate
                will be involved in? <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                    padding: "12px 11px", 
                  },
                }}
                placeholder="Are there any specific projects or initiatives the candidate will be involved in?"
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.anyProjectsOfCandidates}
                helperText={errors.anyProjectsOfCandidates}
                value={anyProjectsOfCandidates}
                onChange={(e) => {
                  const value = e.target.value;
                  setAnyProjectsOfCandidates(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      anyProjectsOfCandidates: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid mt="18px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Qualifications and Skills
            </Typography>
            <Grid mt="19px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What are the essential qualifications, both in terms of
                education and experience?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <VonqTaxonomySelect
                kind="education"
                vonqLink={vonqMapping?.educationLevel}
                onVonqChange={(link) => {
                  updateVonqMapping({ educationLevel: link });
                  if (link?.vonqId) {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      vonqEducationLevel: "",
                    }));
                  }
                }}
                placeholder="Select education level"
                error={!!errors.vonqEducationLevel}
                helperText={errors.vonqEducationLevel}
              />
              <Grid mt={1}></Grid>
              <TextField
                sx={{
                  "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                    padding: "12px 11px",
                  },
                }}
                placeholder="What are the essential qualifications, both in terms of education and experience?"
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.educationAndExperience}
                helperText={errors.educationAndExperience}
                value={educationAndExperience}
                onChange={(e) => {
                  const value = e.target.value;
                  setEducationAndExperience(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      educationAndExperience: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Are there any specific technical skills required for the role?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                    padding: "12px 11px", 
                  },
                }}
                placeholder="Are there any specific technical skills required for the role? "
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.technicalSkillsRequired}
                helperText={errors.technicalSkillsRequired}
                value={technicalSkillsRequired}
                onChange={(e) => {
                  const value = e.target.value;
                  setTechnicalSkillsRequired(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      technicalSkillsRequired: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What soft skills are you looking for in a candidate?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                    padding: "12px 11px", 
                  },
                }}
                placeholder="What soft skills are you looking for in a candidate? "
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.softSkillsRequired}
                helperText={errors.softSkillsRequired}
                value={softSkillsRequired}
                onChange={(e) => {
                  const value = e.target.value;
                  setSoftSkillsRequired(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      softSkillsRequired: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Are there preferred certifications or licenses?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                    padding: "12px 11px", 
                  },
                }}
                placeholder="Are there preferred certifications or licenses?"
                fullWidth
                multiline
                minRows={1}
                maxRows={10}
                className="input-box-custom"
                error={!!errors.preferredCertificationsLicenses}
                helperText={errors.preferredCertificationsLicenses}
                value={preferredCertificationsLicenses}
                onChange={(e) => {
                  const value = e.target.value;
                  setPreferredCertificationsLicenses(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      preferredCertificationsLicenses: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid mt="18px"></Grid>
            <Typography
              color="#273848"
              fontWeight={700}
              lineHeight="14.94px"
              fontSize="15px"
              //   className="input-label-custom"
            >
              Experience Level
            </Typography>
            <Grid mt="19px"></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What level of experience is ideal for this role (entry-level,
                mid-level, senior)? <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <VonqTaxonomySelect
                kind="seniority"
                vonqLink={vonqMapping?.seniority}
                onVonqChange={(link) => {
                  updateVonqMapping({ seniority: link });
                  if (link?.description) {
                    setIdealExperienceLevel(
                      mapSeniorityToExperienceLevel(link.description)
                    );
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      idealExperienceLevel: "",
                    }));
                  } else {
                    setIdealExperienceLevel("");
                  }
                }}
                placeholder="Select experience level"
                error={!!errors.idealExperienceLevel}
                helperText={errors.idealExperienceLevel}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                What is the preferred industry experience?{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <VonqTaxonomySelect
                kind="industry"
                vonqLink={vonqMapping?.industry}
                onVonqChange={(link) => {
                  updateVonqMapping({ industry: link });
                  setIndustrySpecificExperienceRequired(link?.description || "");
                  if (link?.vonqId) {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      vonqIndustry: "",
                    }));
                  }
                }}
                placeholder="Select preferred industry experience"
                error={!!errors.vonqIndustry}
                helperText={errors.vonqIndustry}
              />
            </Grid>
            <Grid mt="40px"></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid xs={isTabs ? 0 : 2}>
                <button
                  type="submit"
                  className="user-scale"
                  // variant="contained"
                  // fullWidth
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #000000",
                    color: "#273848",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    letterSpacing: "0px",
                    textTransform: "none",
                    // padding: "10px 50px",
                    // border:'none',
                    width: " 100%",
                  }}
                  onClick={(event) => handleBack(event)}
                >
                  Back
                </button>
              </Grid>
              <Grid
                item
                xs={isTabs ? 0 : mode ? 3 : 2}
                style={{ maxWidth: "fitContent" }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: loading ? "7px" : "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  {mode ? loading ? <Loader /> : "Save & Next" : "Continue"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default JobProfileSecond;
